import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from "react";
import { SitesContext } from "./SitesContext";
import { DetectionProductContext } from "./DetectionProductContext";
import { AuthContext } from "./AuthContext";
import {
  fetchTrains,
  fetchRailcars,
  fetchDetections,
  fetchRailcarInspections,
  fetchScanSummary
} from "../services/ripAPI";

// Simplified hash for basic data tracking (can be removed if not needed elsewhere)
const createDataHash = (data) => {
  if (!data) return null;
  
  try {
    const hash = {
      trainsCount: data.trains?.length || 0,
      railcarsCount: data.railcars?.length || 0,
      detectionsCount: data.detections?.length || 0,
      inspectionsCount: data.inspections?.length || 0,
      timestamp: Date.now()
    };
    
    return JSON.stringify(hash);
  } catch (error) {
    console.warn('Error creating data hash, using fallback:', error);
    return `${data.trains?.length || 0}-${data.railcars?.length || 0}-${data.detections?.length || 0}-${data.inspections?.length || 0}`;
  }
};

export const SharedDataContext = createContext();

export const SharedDataProvider = ({ children, member, token }) => {
  const { sites } = useContext(SitesContext);
  const { detectionProducts } = useContext(DetectionProductContext);
  const { hasFetched, setHasFetched, resetTrigger, refreshTrigger, apiConfigured } = useContext(AuthContext);
  
  const [selectedTrain, setSelectedTrain] = useState(null);
  const [sharedData, setSharedData] = useState({
    trains: [],
    railcars: [],
    detections: [],
    inspections: [],
    sites: [],
    detectionProducts: [],
  });
  const [fetchError, setFetchError] = useState(null);

  // Train in portal notification state
  const [trainsInPortals, setTrainsInPortals] = useState({
    hasTrains: false,
    details: []
  });



  // Loading states for individual data types  
  const [loadingStates, setLoadingStates] = useState({
    trains: false,
    railcars: false,
    detections: false,
    inspections: false
  });

  // Global loading state to prevent component rebuilds during data fetch
  const [isGlobalLoading, setIsGlobalLoading] = useState(false);

  // Cache for scan summary to enable incremental loading
  const [scanSummaryCache, setScanSummaryCache] = useState({
    totalTrains: 0,
    lastTrainId: 0,
    totalRailcars: 0,
    lastRailcarId: 0,
    totalDetections: 0,
    lastDetectionId: 0,
    totalInspections: 0,
    lastInspectionId: 0
  });

  // Polling configuration
  const POLLING_INTERVAL = 30000; // 30 seconds
  const pollingIntervalRef = useRef(null);
  const lastDataRef = useRef(null);
  const lastHashRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!apiConfigured) {
        console.warn('SharedDataContext: API not yet configured, skipping fetch');
        return;
      }

      if (!member?.acct_id || sites.length === 0 || detectionProducts.length === 0) {
        return;
      }

      try {
        setIsGlobalLoading(true);
        setLoadingStates({
          trains: true,
          railcars: true,
          detections: true,
          inspections: true
        });
        setFetchError(null);

        // Initial load - fetch everything including scan summary
        const [scanSummary, trains, railcars, detections, inspections] = await Promise.all([
          fetchScanSummary(member.acct_id),
          fetchTrains(member.acct_id, 0), // Start from 0 for initial load
          fetchRailcars(member.acct_id, 0), 
          fetchDetections(member.acct_id, 0),
          fetchRailcarInspections(member.acct_id)
        ]);


        // Update scan summary cache with the latest values
        setScanSummaryCache({
          totalTrains: scanSummary.totalTrains || 0,
          lastTrainId: scanSummary.lastTrainId || 0,
          totalRailcars: scanSummary.totalRailcars || 0,
          lastRailcarId: scanSummary.lastRailcarId || 0,
          totalDetections: scanSummary.totalDetections || 0,
          lastDetectionId: scanSummary.lastDetectionId || 0,
          totalInspections: scanSummary.totalInspections || 0,
          lastInspectionId: scanSummary.lastInspectionId || 0
        });

        // Update trains in portals state
        const newTrainsInPortals = {
          hasTrains: scanSummary.trainsInPortals || false,
          details: scanSummary.trainsInPortalsDetails || []
        };
        setTrainsInPortals(newTrainsInPortals);

        // Single atomic update - only one state change instead of 4-5
        setSharedData({
          trains,
          railcars,
          detections,
          inspections,
          sites,
          detectionProducts,
        });

        setFetchError(null);

        setLoadingStates({
          trains: false,
          railcars: false,
          detections: false,
          inspections: false
        });

        setIsGlobalLoading(false);

        // Update hash reference for future comparisons
        const newData = { trains, railcars, detections, inspections };
        lastDataRef.current = newData;
        lastHashRef.current = createDataHash(newData);

      } catch (error) {
        console.error("Error fetching data:", error);
        setLoadingStates({
          trains: false,
          railcars: false,
          detections: false,
          inspections: false
        });
        setIsGlobalLoading(false);
        setFetchError(error instanceof Error ? error : new Error("Failed to load dashboard data"));
      } finally {
        setHasFetched(true);
      }
    };

    // Simple, clear conditions - just like the other contexts
    if (resetTrigger > 0) {
      return;
    }
    
    if (apiConfigured && sites.length > 0 && detectionProducts.length > 0 && (!hasFetched || refreshTrigger > 0)) {
      fetchData();
    }
    // Using .length properties to avoid reference comparison issues with arrays
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiConfigured, sites.length, detectionProducts.length, hasFetched, resetTrigger, refreshTrigger, member?.acct_id, setHasFetched]);

  const resetSharedData = useCallback(() => {
    setSharedData({
      trains: [],
      railcars: [],
      detections: [],
      inspections: [],
      sites: [],
      detectionProducts: [],
    });
    setSelectedTrain(null);
    setLoadingStates({
      trains: false,
      railcars: false,
      detections: false,
      inspections: false
    });
    setFetchError(null);
    
    // Reset scan summary cache
    setScanSummaryCache({
      totalTrains: 0,
      lastTrainId: 0,
      totalRailcars: 0,
      lastRailcarId: 0,
      totalDetections: 0,
      lastDetectionId: 0,
      totalInspections: 0,
      lastInspectionId: 0
    });

    // Reset trains in portals state
    setTrainsInPortals({
      hasTrains: false,
      details: []
    });
    
    lastDataRef.current = null;
    lastHashRef.current = null;
  }, []);

  // Incremental data update function for polling - only fetches changed data
  const checkForUpdatesAndFetch = useCallback(async (silent = true) => {
    if (!member?.acct_id || sites.length === 0 || detectionProducts.length === 0) {
      return;
    }

    try {
      // First, check the scan summary to see if anything changed
      const currentSummary = await fetchScanSummary(member.acct_id);
      
      // Compare with cached values to see what changed
      const trainsChanged = currentSummary.totalTrains !== scanSummaryCache.totalTrains;
      const railcarsChanged = currentSummary.totalRailcars !== scanSummaryCache.totalRailcars;
      const detectionsChanged = currentSummary.totalDetections !== scanSummaryCache.totalDetections;
      const inspectionsChanged = currentSummary.totalInspections !== scanSummaryCache.totalInspections;
      const trainsInPortalsChanged = 
        currentSummary.trainsInPortals !== trainsInPortals.hasTrains ||
        JSON.stringify(currentSummary.trainsInPortalsDetails || []) !== JSON.stringify(trainsInPortals.details);

      if (!trainsChanged && !railcarsChanged && !detectionsChanged && !inspectionsChanged && !trainsInPortalsChanged) {
        // No changes detected, skip the expensive data fetch
        if (!silent) {
          console.log('No changes detected in scan summary, skipping data fetch');
        }
        return;
      }

      // Update trains in portals state if it changed (even if no data fetch needed)
      if (trainsInPortalsChanged) {
        const newTrainsInPortals = {
          hasTrains: currentSummary.trainsInPortals || false,
          details: currentSummary.trainsInPortalsDetails || []
        };
        setTrainsInPortals(newTrainsInPortals);
      }

      if (!silent) {
        console.log('Changes detected:', {
          trains: trainsChanged,
          railcars: railcarsChanged,
          detections: detectionsChanged,
          inspections: inspectionsChanged
        });
      }

      // Fetch only the changed data using last known IDs
      const fetchPromises = [];
      
      if (trainsChanged) {
        fetchPromises.push(fetchTrains(member.acct_id, scanSummaryCache.lastTrainId));
      }
      if (railcarsChanged) {
        fetchPromises.push(fetchRailcars(member.acct_id, scanSummaryCache.lastRailcarId));
      }
      if (detectionsChanged) {
        fetchPromises.push(fetchDetections(member.acct_id, scanSummaryCache.lastDetectionId));
      }
      if (inspectionsChanged) {
        // Note: inspections API doesn't support incremental loading yet
        fetchPromises.push(fetchRailcarInspections(member.acct_id));
      }

      if (fetchPromises.length === 0) return;

      const results = await Promise.all(fetchPromises);
      
      // Update shared data by appending new records to existing ones
      setSharedData(prevData => {
        const newData = { ...prevData };
        let resultIndex = 0;

        if (trainsChanged && results[resultIndex]) {
          newData.trains = [...prevData.trains, ...results[resultIndex]];
          resultIndex++;
        }
        if (railcarsChanged && results[resultIndex]) {
          newData.railcars = [...prevData.railcars, ...results[resultIndex]];
          resultIndex++;
        }
        if (detectionsChanged && results[resultIndex]) {
          newData.detections = [...prevData.detections, ...results[resultIndex]];
          resultIndex++;
        }
        if (inspectionsChanged && results[resultIndex]) {
          // For inspections, replace entirely since no incremental support
          newData.inspections = results[resultIndex];
          resultIndex++;
        }

        return newData;
      });

      // Update the scan summary cache with new values
      setScanSummaryCache({
        totalTrains: currentSummary.totalTrains || 0,
        lastTrainId: currentSummary.lastTrainId || 0,
        totalRailcars: currentSummary.totalRailcars || 0,
        lastRailcarId: currentSummary.lastRailcarId || 0,
        totalDetections: currentSummary.totalDetections || 0,
        lastDetectionId: currentSummary.lastDetectionId || 0,
        totalInspections: currentSummary.totalInspections || 0,
        lastInspectionId: currentSummary.lastInspectionId || 0
      });

      // Note: trainsInPortals state is already updated earlier in this function (lines 259-265)
      // when trainsInPortalsChanged is true, so no need to update it again here

      if (!silent) {
        console.log('Incremental data update completed');
      }

    } catch (error) {
      if (!silent) {
        console.error("Error in incremental data update:", error);
      }
    }
  }, [member, sites, detectionProducts, scanSummaryCache, trainsInPortals]);

  const refreshData = useCallback(async (silent = false) => {
    if (member && member.acct_id && sites.length > 0 && detectionProducts.length > 0) {
      if (silent) {
        // For background polling, use incremental updates
        await checkForUpdatesAndFetch(true);
      } else {
        // For manual refreshes, do a full reload
        try {
          setFetchError(null);
          // Parallel API calls for better performance
          const [scanSummary, trains, railcars, detections, inspections] = await Promise.all([
            fetchScanSummary(member.acct_id),
            fetchTrains(member.acct_id, 0),
            fetchRailcars(member.acct_id, 0),
            fetchDetections(member.acct_id, 0),
            fetchRailcarInspections(member.acct_id)
          ]);
          
          // Update scan summary cache
          setScanSummaryCache({
            totalTrains: scanSummary.totalTrains || 0,
            lastTrainId: scanSummary.lastTrainId || 0,
            totalRailcars: scanSummary.totalRailcars || 0,
            lastRailcarId: scanSummary.lastRailcarId || 0,
            totalDetections: scanSummary.totalDetections || 0,
            lastDetectionId: scanSummary.lastDetectionId || 0,
            totalInspections: scanSummary.totalInspections || 0,
            lastInspectionId: scanSummary.lastInspectionId || 0
          });

          // Update trains in portals state
          const newTrainsInPortalsManual = {
            hasTrains: scanSummary.trainsInPortals || false,
            details: scanSummary.trainsInPortalsDetails || []
          };
          setTrainsInPortals(newTrainsInPortalsManual);

          // Full data update for manual refresh
          setSharedData(prevData => ({
            ...prevData,
            trains,
            railcars,
            detections,
            inspections,
          }));
          
          const newData = { trains, railcars, detections, inspections };
          lastDataRef.current = newData;
          lastHashRef.current = createDataHash(newData);
        } catch (error) {
          console.error("Error refreshing data:", error);
          setFetchError(error instanceof Error ? error : new Error("Failed to refresh dashboard data"));
        }
      }
    }
  }, [member, sites, detectionProducts, checkForUpdatesAndFetch]);

  // Watch for reset trigger from AuthContext
  useEffect(() => {
    if (resetTrigger > 0) {
      resetSharedData();
    }
  }, [resetTrigger, resetSharedData]);

  // Watch for refresh trigger from AuthContext (for account switching)
  useEffect(() => {
    if (refreshTrigger > 0 && sites.length > 0 && detectionProducts.length > 0 && member && member.acct_id) {
      const fetchData = async () => {
        try {
          // Parallel API calls for better performance including scan summary
          const [scanSummary, trains, railcars, detections, inspections] = await Promise.all([
            fetchScanSummary(member.acct_id),
            fetchTrains(member.acct_id, 0),
            fetchRailcars(member.acct_id, 0),
            fetchDetections(member.acct_id, 0),
            fetchRailcarInspections(member.acct_id)
          ]);
          
          // Update scan summary cache
          setScanSummaryCache({
            totalTrains: scanSummary.totalTrains || 0,
            lastTrainId: scanSummary.lastTrainId || 0,
            totalRailcars: scanSummary.totalRailcars || 0,
            lastRailcarId: scanSummary.lastRailcarId || 0,
            totalDetections: scanSummary.totalDetections || 0,
            lastDetectionId: scanSummary.lastDetectionId || 0,
            totalInspections: scanSummary.totalInspections || 0,
            lastInspectionId: scanSummary.lastInspectionId || 0
          });

          // Update trains in portals state
          const newTrainsInPortalsRefresh = {
            hasTrains: scanSummary.trainsInPortals || false,
            details: scanSummary.trainsInPortalsDetails || []
          };
          setTrainsInPortals(newTrainsInPortalsRefresh);
          
          setSharedData({
            trains,
            railcars,
            detections,
            sites,
            inspections,
            detectionProducts,
          });
          
          const newData = { trains, railcars, detections, inspections };
          lastDataRef.current = newData;
          lastHashRef.current = createDataHash(newData);
          setHasFetched(true);
          setFetchError(null);
        } catch (error) {
          console.error("Error refreshing data:", error);
          setFetchError(error instanceof Error ? error : new Error("Failed to refresh dashboard data"));
        }
      };
      fetchData();
    }
  }, [refreshTrigger, sites, detectionProducts, member, setHasFetched]);

  // Set up periodic polling when data is available and user is authenticated
  useEffect(() => {
    if (hasFetched && member && member.acct_id && sites.length > 0 && detectionProducts.length > 0) {
      const startPolling = () => {
        pollingIntervalRef.current = setInterval(() => {
          refreshData(true); // silent = true for background polling
        }, POLLING_INTERVAL);
      };

      const stopPolling = () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };

      // Start polling
      startPolling();

      // Handle visibility change to pause/resume polling
      const handleVisibilityChange = () => {
        if (document.hidden) {
          stopPolling();
        } else {
          stopPolling();
          startPolling();
          // Refresh immediately when tab becomes visible
          refreshData(true);
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Cleanup function
      return () => {
        stopPolling();
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [hasFetched, member, sites, detectionProducts, refreshData, POLLING_INTERVAL]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Update a specific detection in shared data
  const updateDetectionInSharedData = useCallback((detectionId, updates) => {
    setSharedData(prevData => ({
      ...prevData,
      detections: prevData.detections.map(detection => 
        detection.id === detectionId 
          ? { ...detection, ...updates }
          : detection
      )
    }));
  }, []);

  return (
    <SharedDataContext.Provider
      value={{ 
        sharedData, 
        selectedTrain, 
        setSelectedTrain, 
        resetSharedData, 
        refreshData,
        loadingStates,
        isGlobalLoading,
        isAnyLoading: Object.values(loadingStates).some(loading => loading),
        trainsInPortals,
        fetchError,
        updateDetectionInSharedData
      }}
    >
      {children}
    </SharedDataContext.Provider>
  );
};
